<template>
  <div class="virtual-account">
    <!-- Header Card -->
    <div class="virtual-account__header">
      <div class="virtual-account__header-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9L12 4L21 9V11H3V9Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
          <path d="M5 11V17M9 11V17M15 11V17M19 11V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M2 20H22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M2 17H22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <h3>{{ getCurrencySymbol(currency) }} Virtual Account</h3>
    </div>

    <!-- Account Details -->
    <div class="virtual-account__details">
      <!-- Account Name -->
      <div class="virtual-account__row">
        <div class="virtual-account__info">
          <span class="virtual-account__label">Account Name</span>
          <span class="virtual-account__value">GETEQUITY LIMITED</span>
        </div>
        <button class="virtual-account__copy-btn" @click="copyText('account name')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11V3.5C3 3.22386 3.22386 3 3.5 3H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Copy
        </button>
        <input type="hidden" ref="accountName" value="GETEQUITY LIMITED" />
      </div>

      <!-- Account Number -->
      <div class="virtual-account__row">
        <div class="virtual-account__info">
          <span class="virtual-account__label">Account Number</span>
          <span class="virtual-account__value virtual-account__value--highlight">
            {{ virtualAccount?.account_number }}
          </span>
        </div>
        <button class="virtual-account__copy-btn" @click="copyText('account number')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11V3.5C3 3.22386 3.22386 3 3.5 3H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Copy
        </button>
        <input type="hidden" ref="accountNumber" :value="virtualAccount?.account_number" />
      </div>

      <!-- Bank Name -->
      <div class="virtual-account__row">
        <div class="virtual-account__info">
          <span class="virtual-account__label">Bank Name</span>
          <span class="virtual-account__value">{{ capitalize(virtualAccount?.bank_name) }}</span>
        </div>
        <button class="virtual-account__copy-btn" @click="copyText('bank name')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11V3.5C3 3.22386 3.22386 3 3.5 3H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Copy
        </button>
        <input type="hidden" ref="bankName" :value="capitalize(virtualAccount?.bank_name)" />
      </div>

      <!-- Bank Code -->
      <div class="virtual-account__row">
        <div class="virtual-account__info">
          <span class="virtual-account__label">Bank Code</span>
          <span class="virtual-account__value">{{ virtualAccount?.bank_code }}</span>
        </div>
        <button class="virtual-account__copy-btn" @click="copyText('bank code')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11V3.5C3 3.22386 3.22386 3 3.5 3H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Copy
        </button>
        <input type="hidden" ref="bankCode" :value="virtualAccount?.bank_code" />
      </div>

      <!-- Address (for non-NGN) -->
      <div v-if="currency !== 'NGN'" class="virtual-account__row">
        <div class="virtual-account__info">
          <span class="virtual-account__label">Address</span>
          <span class="virtual-account__value virtual-account__value--small">
            {{ virtualAccount?.address }}
          </span>
        </div>
        <button class="virtual-account__copy-btn" @click="copyText('address')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M3 11V3.5C3 3.22386 3.22386 3 3.5 3H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Copy
        </button>
        <textarea ref="address" :value="virtualAccount?.address" class="tw-sr-only" readonly />
      </div>
    </div>

    <!-- Instructions Card -->
    <div class="virtual-account__instructions">
      <div class="virtual-account__instructions-header">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M9 5V9L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Payment Instructions</span>
      </div>
      <div class="virtual-account__instructions-content">
        <p>
          Transfer exactly <strong>{{ formatAmountToDollar(fundDetails.totalAmount, 2, currency) }}</strong>
          to the account above with reference code:
        </p>
        <div class="virtual-account__reference">
          <code>{{ virtualAccount?.customer_reference }}</code>
        </div>
        <p class="virtual-account__warning">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 5V7.5M7 9.5H7.005M12.5 7C12.5 10.0376 10.0376 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Missing reference code may delay your transaction.
        </p>
        <p class="virtual-account__note">
          Deposits may take 1-48 hours to reflect.
        </p>
      </div>
    </div>

    <!-- Done Button -->
    <button class="virtual-account__done-btn" @click="$emit('close', 'Fund Wallet')">
      Done
    </button>
  </div>
</template>

<script>
import { capitalize, formatAmountToDollar, getCurrencySymbol } from "@/utils/helpers";
import { getItem } from "@/utils/storage.js";
import { mapActions, mapState } from "vuex";

export default {
  name: "DedicatedVirtualAccountForm",

  data() {
    return {
      fxLoading: false,
      userDetails: {},
      name: "",
    };
  },

  computed: {
    ...mapState({
      virtualAccount: (state) => state.organisationModule.virtualAccount,
      currency: (state) => state.organisationModule.currency,
      exchangeRate: (state) => state.organisationModule.exchangeRate,
    }),
    fundDetails: () => getItem("fundPayload"),
  },

  mounted() {
    this.getRate();
    this.userDetails = getItem("org_details");
    this.name = `${capitalize(this.userDetails.name)}`;
  },

  methods: {
    ...mapActions(["showToast"]),
    ...mapActions("organisationModule", ["getExchangeRate"]),

    capitalize,
    formatAmountToDollar,
    getCurrencySymbol,

    async getRate() {
      this.fxLoading = true;
      const exchangeQuery = { from: "USD", to: this.currency };
      await this.getExchangeRate(exchangeQuery);
      this.fxLoading = false;
    },

    copyText(text) {
      let message = "";
      let content = "";
      switch (text) {
        case "account number":
          content = this.virtualAccount?.account_number;
          message = "Account number";
          break;
        case "account name":
          content = "GETEQUITY LIMITED";
          message = "Account name";
          break;
        case "bank name":
          content = this.virtualAccount?.bank_name;
          message = "Bank name";
          break;
        case "bank code":
          content = this.virtualAccount?.bank_code;
          message = "Bank code";
          break;
        case "address":
          content = this.virtualAccount?.address;
          message = "Address";
          break;
        default:
          break;
      }

      navigator.clipboard.writeText(content).then(() => {
        this.showToast({
          description: `${message} copied`,
          display: true,
          type: "success",
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$warning: #F59E0B;
$warning-light: #FFFBEB;

.virtual-account {
  padding-top: 16px;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: $accent-light;
    border-radius: 10px;
    margin-bottom: 20px;
  }

  &__header-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 10px;
    color: $accent-primary;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }

  &__details {
    border: 1px solid $border-color;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid $border-color;

    &:last-child {
      border-bottom: none;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  &__label {
    font-size: 12px;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__value {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;

    &--highlight {
      font-size: 18px;
      font-weight: 600;
    }

    &--small {
      font-size: 13px;
      line-height: 1.4;
    }
  }

  &__copy-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: $bg-subtle;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: $text-secondary;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: $accent-light;
      color: $accent-primary;
    }
  }

  &__instructions {
    background: $warning-light;
    border: 1px solid #FDE68A;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 24px;
  }

  &__instructions-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    color: $warning;
    margin-bottom: 12px;
  }

  &__instructions-content {
    p {
      font-size: 14px;
      color: $text-secondary;
      margin: 0 0 12px;
      line-height: 1.5;

      strong {
        color: $text-primary;
      }
    }
  }

  &__reference {
    background: white;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;

    code {
      font-size: 14px;
      font-weight: 500;
      color: $accent-primary;
      word-break: break-all;
    }
  }

  &__warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    color: #DC2626 !important;

    svg {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  &__note {
    color: $text-muted !important;
    font-size: 13px !important;
  }

  &__done-btn {
    width: 100%;
    height: 48px;
    background: $btn-primary;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: $btn-primary-hover;
    }
  }
}
</style>
